import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useInRouterContext, useLocation } from 'react-router-dom';
import { LOCAL_STORAGE } from '../../constants';
import { getLocalStorageItem } from '../../utils';

const SCRIPT_ID = 'ssc-ai-widget-script';

function loadWidgetScript(src) {
    return new Promise((resolve, reject) => {
        if (document.getElementById(SCRIPT_ID)) return resolve();
        const s = document.createElement('script');
        s.id = SCRIPT_ID;
        s.src = src;
        s.async = true;
        s.onload = () => resolve();
        s.onerror = (e) => reject(e);
        document.head.appendChild(s);
    });
}

// Helper component to safely use useLocation only when inside a Router
const RouterLocationSensor = ({ onLocationChange }) => {
    const location = useLocation();
    useEffect(() => {
        onLocationChange(location);
    }, [location, onLocationChange]);
    return null;
};

export default function AiAssistantWidget() {
    const [open, setOpen] = useState(false);
    const [ready, setReady] = useState(false);
    const containerRef = useRef(null);
    const closeBtnRef = useRef(null);
    const routerReady = useInRouterContext();
    const [routerLocation, setRouterLocation] = useState(null);

    // Use the captured router location if available
    const location = routerReady ? routerLocation : null;

    // Host context derived from existing frontend state so the Vue assistant
    // can align its initial view and account context with the React app.
    const roleCode = getLocalStorageItem(LOCAL_STORAGE.ROLE_CODE);
    const accountId = getLocalStorageItem(LOCAL_STORAGE.ACCOUNT_ID);
    const pagePath = (location && location.pathname) || (typeof window !== 'undefined' ? window.location.pathname : '');

    useEffect(() => {
        if (typeof window === 'undefined') return undefined;
        const detail = {
            role: roleCode || '',
            accountId: accountId || '',
            page: pagePath || ''
        };
        window.dispatchEvent(new CustomEvent('ssc-ai-context', { detail }));
        return undefined;
    }, [roleCode, accountId, pagePath]);

    const sources = useMemo(() => {
        const list = [];
        // Primary: explicit widget URL, if configured
        // eslint-disable-next-line no-undef
        const envSrc = process.env.REACT_APP_AI_WIDGET_SRC;
        if (envSrc && typeof envSrc === 'string') {
            list.push(envSrc);
        }
        // Fallbacks: derive from backend base URL so the backend remains the
        // single source of truth for the widget bundle. This also avoids
        // accidentally loading a stale copy from the React public assets.
        // eslint-disable-next-line no-undef
        const base = process.env.REACT_APP_BASE_URL;
        if (base && typeof base === 'string') {
            const trimmed = base.replace(/\/+$/, '');
            list.push(`${trimmed}/assistant-widget/ai-widget.js`);
            list.push(`${trimmed}/ai-widget/ai-widget.js`);
        }
        return list;
    }, []);

    // Lazy load widget script on first open
    useEffect(() => {
        if (!open || ready) return;
        if (!sources || sources.length === 0) return; // no configured source; stay silent
        let cancelled = false;
        (async () => {
            for (const src of sources) {
                try {
                    await loadWidgetScript(src);
                    if (!cancelled) setReady(true);
                    return;
                } catch (e) {
                    // try next source (ad/tracker blockers can block specific paths)
                    continue;
                }
            }
            // still not ready; keep panel closed state
            if (!cancelled) setReady(false);
        })();
        return () => { cancelled = true; };
    }, [open, ready, sources]);

    // Close on Escape
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open]);

    // Move focus to Close on open for accessibility
    useEffect(() => {
        if (open && closeBtnRef.current) {
            try { closeBtnRef.current.focus(); } catch (e) { console.warn('focus failed', e); }
        }
    }, [open]);

    const toggle = useCallback(() => setOpen((v) => !v), []);
    const close = useCallback(() => setOpen(false), []);

    return (
        <>
            {routerReady && <RouterLocationSensor onLocationChange={setRouterLocation} />}
            <button
                type="button"
                aria-haspopup="dialog"
                aria-expanded={open ? 'true' : 'false'}
                aria-controls="ai-assistant-panel"
                onClick={toggle}
                style={{
                    position: 'fixed',
                    right: 16,
                    bottom: 88,
                    zIndex: 2147483000,
                    padding: '10px 12px',
                    borderRadius: 20,
                    border: '1px solid #e5e7eb',
                    background: '#fff',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                    cursor: 'pointer'
                }}
                title="Open Assistant"
            >
                Assistant
            </button>

            {open && (
                <div
                    role="dialog"
                    aria-modal="true"
                    id="ai-assistant-panel"
                    style={{
                        position: 'fixed',
                        inset: 0,
                        zIndex: 2147483001,
                        background: 'rgba(15,23,42,0.55)',
                        backdropFilter: 'blur(4px)',
                        WebkitBackdropFilter: 'blur(4px)',
                        display: 'flex',
                        justifyContent: 'flex-end'
                    }}
                    onClick={(e) => { if (e.target === e.currentTarget) close(); }}
                >
                    <div
                        ref={containerRef}
                        style={{
                            width: 'min(75vw, 1280px)',
                            maxWidth: '100%',
                            height: '100%',
                            background: '#fff',
                            borderLeft: '1px solid #e5e7eb',
                            boxShadow: '0 10px 40px rgba(15,23,42,0.35)',
                            borderRadius: '12px 0 0 12px',
                            overflow: 'auto'
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '10px 14px',
                                borderBottom: '1px solid #e5e7eb',
                                background: 'linear-gradient(90deg, #0f172a 0%, #111827 40%, #1e293b 100%)',
                                color: '#f9fafb'
                            }}
                        >
                            <div style={{ fontWeight: 600, fontSize: 14 }}>Safety Services Assistant</div>
                            <button
                                ref={closeBtnRef}
                                type="button"
                                onClick={close}
                                aria-label="Close"
                                style={{
                                    border: '1px solid rgba(148,163,184,0.9)',
                                    background: 'rgba(15,23,42,0.9)',
                                    color: '#e5e7eb',
                                    borderRadius: 999,
                                    padding: '6px 14px',
                                    fontSize: 12,
                                    cursor: 'pointer'
                                }}
                            >
                                Close
                            </button>
                        </div>
                        <div style={{ height: 'calc(100% - 42px)' }}>
                            {ready ? (
                                <ssc-ai-console
                                    data-role={roleCode || ''}
                                    data-account-id={accountId || ''}
                                    data-page={pagePath || ''}
                                />
                            ) : (
                                <div style={{ padding: 16 }}>Loading…</div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
